/**
 * Fantasy Console 0 - Shared Icons
 *
 * SVG icons used across the editor. All shared icons live here.
 */

// ============================================
// Tool Icons (used in ToolBar)
// ============================================

export function PencilIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M11.5 2.5l2 2-8 8H3.5v-2l8-8z' />
      <path d='M10 4l2 2' />
    </svg>
  )
}

export function FillIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M2 10l4-8 4 8' />
      <path d='M3 8h6' />
      <path
        d='M12 6c0 0 2 2 2 4s-1 2-2 2-2 0-2-2 2-4 2-4z'
        fill='currentColor'
      />
    </svg>
  )
}

export function RectIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='3' y='4' width='10' height='8' />
    </svg>
  )
}

export function EraserIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M5 13h8' />
      <path d='M3.5 10.5l7-7 2 2-7 7-2.5.5.5-2.5z' />
    </svg>
  )
}

export function PickerIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M3 13l2-6 4 4-6 2z' />
      <path d='M9 7l4-4' />
      <circle cx='12' cy='4' r='1' fill='currentColor' />
    </svg>
  )
}

// ============================================
// Action Icons (used in action groups)
// ============================================

export function ClearIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='2' y='2' width='10' height='10' rx='1' />
      <path d='M4 4l6 6M10 4l-6 6' />
    </svg>
  )
}

export function CopyIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='5' y='5' width='7' height='7' rx='1' />
      <path d='M9 5V3a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h2' />
    </svg>
  )
}

export function PasteIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect x='3' y='2' width='8' height='10' rx='1' />
      <path d='M6 2V1h2v1' />
      <path d='M5 7h4M5 9h4' />
    </svg>
  )
}

export function ImportIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M7 2v8M3 6l4 4 4-4' />
      <path d='M2 12h10' />
    </svg>
  )
}

export function ExportIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M7 10V2M3 6l4-4 4 4' />
      <path d='M2 12h10' />
    </svg>
  )
}

// ============================================
// UI Icons (used in header, cards, modals, etc.)
// ============================================

export function NewIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M8 3v10M3 8h10' />
    </svg>
  )
}

export function CloseIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
    >
      <path d='M4 4l8 8m0-8L4 12' />
    </svg>
  )
}

export function ErrorIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='currentColor'>
      <path d='M8 1a7 7 0 100 14A7 7 0 008 1zM7 4h2v5H7V4zm0 6h2v2H7v-2z' />
    </svg>
  )
}

export function CloudUploadIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.25'
    >
      <path d='M12.2 13.2H5a3.6 3.6 0 01-.6-7.152 4.8 4.8 0 018.8 1.752A3 3 0 0112.2 13.2z' />
      <path d='M 8 8 v 5 M 6 10 l 2 -2 2 2' />
    </svg>
  )
}

const spinnerStyle = { animation: 'spin 0.8s linear infinite' }
const spinnerKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`
let spinnerStyleInjected = false

export function SpinnerIcon() {
  if (!spinnerStyleInjected && typeof document !== 'undefined') {
    const style = document.createElement('style')
    style.textContent = spinnerKeyframes
    document.head.appendChild(style)
    spinnerStyleInjected = true
  }
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      style={spinnerStyle}
    >
      <path d='M8 2v3M8 11v3M2 8h3M11 8h3' />
    </svg>
  )
}

export function UserIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <circle cx='8' cy='5' r='3' />
      <path d='M2 14c0-3 2.5-5 6-5s6 2 6 5' />
    </svg>
  )
}

export function ChevronDownIcon() {
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M3 4.5l3 3 3-3' />
    </svg>
  )
}

export function ImagePlaceholderIcon() {
  return (
    <svg
      width='32'
      height='32'
      viewBox='0 0 32 32'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <rect x='4' y='4' width='24' height='24' rx='2' />
      <circle cx='11' cy='11' r='2' />
      <path d='M4 22l6-6 4 4 8-8 6 6' />
    </svg>
  )
}

export function HeartIcon({ filled }: { filled?: boolean }) {
  if (filled) {
    return (
      <svg
        width='16'
        height='16'
        viewBox='0 0 16 16'
        fill='currentColor'
        stroke='currentColor'
        strokeWidth='1.5'
      >
        <path d='M8 14s-5.5-3.5-5.5-7A3 3 0 018 4.5 3 3 0 0113.5 7c0 3.5-5.5 7-5.5 7z' />
      </svg>
    )
  }
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M8 14s-5.5-3.5-5.5-7A3 3 0 018 4.5 3 3 0 0113.5 7c0 3.5-5.5 7-5.5 7z' />
    </svg>
  )
}

export function SignOutIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M6 8h8' />
    </svg>
  )
}

export function MoreIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' fill='currentColor'>
      <circle cx='8' cy='3' r='1.5' />
      <circle cx='8' cy='8' r='1.5' />
      <circle cx='8' cy='13' r='1.5' />
    </svg>
  )
}

export function ForkIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <circle cx='5' cy='3' r='2' />
      <circle cx='11' cy='3' r='2' />
      <circle cx='8' cy='13' r='2' />
      <path d='M5 5v2c0 1.5 1 3 3 3s3-1.5 3-3V5' />
    </svg>
  )
}

export function EditIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M11.5 2.5l2 2M2 14l1-4L11 2l2 2-8 8-4 1z' />
    </svg>
  )
}

export function TrashIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M1 3h12M5 3V1h4v2M3 3v10a1 1 0 001 1h6a1 1 0 001-1V3' />
      <path d='M5.5 6v5M8.5 6v5' />
    </svg>
  )
}

export function RefreshIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
    >
      <path d='M14 8a6 6 0 11-1.5-4M14 2v4h-4' />
    </svg>
  )
}

export function SunIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
    >
      <circle cx='8' cy='8' r='3' />
      <path d='M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41' />
    </svg>
  )
}

export function MoonIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 16 16'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M13.5 8.5a5.5 5.5 0 01-7.5-7.5 6 6 0 107.5 7.5z' />
    </svg>
  )
}

export function ShareIcon() {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 14 14'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='10.5' cy='2.5' r='1.5' />
      <circle cx='3.5' cy='7' r='1.5' />
      <circle cx='10.5' cy='11.5' r='1.5' />
      <path d='M5 7.9l4 2.7M5 6.1l4-2.7' />
    </svg>
  )
}
